import { useEffect, useRef, useState } from 'react';
import { todayKey } from '../state';
import { CSV_EXPORTS, downloadText, exportableState } from '../csvExport';
import { FONT_MONO } from '../theme';
import { Button, Card, Input } from '../ui';

const STALE_MS = 2 * 86400000; // a nightly backup older than two days is a problem

async function readBackupFile(file) {
  if (/\.gz$/i.test(file.name)) {
    if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot open .gz files; unzip it first.');
    return new Response(file.stream().pipeThrough(new DecompressionStream('gzip'))).text();
  }
  return file.text();
}

const fmtBytes = b => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

/* Export JSON/CSV, import a file and restore a server backup. Restores go
 * through the API (compare-and-swap, journaled), never by copying the .db. */
export function BackupCard({ t, state, token, onRestore }) {
  const [server, setServer] = useState(null); // { last_backup, backups } once loaded; hidden on any error
  const [pending, setPending] = useState(null); // { body, summary } after a dry run
  const [confirmText, setConfirmText] = useState('');
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    fetch('/api/fitness/backups', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(res => { if (!cancelled && res?.success) setServer(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const preview = async (body) => {
    setBusy(true); setMessage(null);
    const res = await onRestore({ ...body, mode: 'merge', dry_run: true });
    setBusy(false);
    if (res?.success) { setPending({ body, summary: res.summary }); setConfirmText(''); }
    else setMessage(res?.message || 'Could not read that backup');
  };

  const run = async (mode) => {
    setBusy(true);
    const res = await onRestore({ ...pending.body, mode });
    setBusy(false);
    setPending(null);
    setMessage(res?.success ? (res.summary || 'Restored') : (res?.message || 'Restore failed'));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const data = JSON.parse(await readBackupFile(file));
      await preview({ source: 'file', data });
    } catch (err) {
      setMessage(err?.message?.includes('gz') ? err.message : 'That file is not a fitness backup (JSON).');
    }
  };

  const last = server?.last_backup;
  const stale = last && (!last.ok || Date.now() - last.at > STALE_MS);

  return (
    <Card t={t} padding={20}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 14 }}>Backup</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button t={t} size="md" onClick={() => downloadText(`xceller-fitness-${todayKey()}.json`,
          JSON.stringify(exportableState(state), null, 2), 'application/json')}>Export JSON</Button>
        <Button t={t} size="md" disabled={!token || busy} onClick={() => fileRef.current?.click()}>Import…</Button>
        <input ref={fileRef} type="file" accept=".json,.gz,application/json" onChange={onFile} style={{ display: 'none' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {CSV_EXPORTS.map(x => (
          <Button key={x.id} t={t} size="sm" variant="ghost" style={{ minHeight: 40 }}
            onClick={() => downloadText(`xceller-${x.id}-${todayKey()}.csv`, x.build(state), 'text/csv')}>
            {x.label} CSV
          </Button>
        ))}
      </div>

      {server && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.divider}` }}>
          <div style={{ fontSize: 12, color: stale || !last ? t.danger : t.textMuted }}>
            {last
              ? `Last server backup: ${new Date(last.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}${last.ok ? '' : ` · failed${last.error ? `: ${last.error}` : ''}`}`
              : 'No server backup yet'}
          </div>
          {(server.backups || []).slice(0, 7).map(b => (
            <div key={b.date} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
              <span style={{ flex: 1, fontSize: 13, fontFamily: FONT_MONO, color: t.text }}>{b.date}</span>
              <span style={{ fontSize: 11, color: t.textFaint }}>{fmtBytes(b.bytes)}</span>
              <Button t={t} size="sm" variant="ghost" disabled={busy} style={{ minHeight: 40 }}
                onClick={() => preview({ source: 'server', date: b.date })}>Restore…</Button>
            </div>
          ))}
        </div>
      )}

      {pending && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, color: t.text, lineHeight: 1.45 }}>{pending.summary}</div>
          <Button t={t} variant="primary" disabled={busy} style={{ minHeight: 44, justifyContent: 'center' }} onClick={() => run('merge')}>
            Add missing days only
          </Button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input t={t} value={confirmText} placeholder="Type REPLACE to replace everything" onChange={e => setConfirmText(e.target.value)} />
            <Button t={t} variant="danger" disabled={busy || confirmText !== 'REPLACE'} style={{ minHeight: 44 }} onClick={() => run('replace')}>
              Replace
            </Button>
          </div>
          <Button t={t} variant="ghost" style={{ minHeight: 40 }} onClick={() => setPending(null)}>Cancel</Button>
        </div>
      )}
      {message && <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10 }}>{message}</div>}
    </Card>
  );
}
